// Resource verification and enrichment
export const verifyResource = async (url) => {
  try {
    const response = await fetch(url, { 
      method: 'HEAD',
      mode: 'no-cors' // Many sites block CORS, so we'll do best effort
    })
    return true // If no error thrown, assume valid
  } catch (error) {
    console.warn(`Resource verification failed for ${url}:`, error)
    return false
  }
}

export const enrichResources = async (roadmap) => {
  const enrichedRoadmap = { ...roadmap }
  
  for (const week of enrichedRoadmap.weeks) {
    for (const task of week.tasks) {
      for (const resource of task.resources) {
        // Mark as placeholder if URL seems invalid
        if (!resource.url || !resource.url.startsWith('http')) {
          resource.is_placeholder = true
          continue
        }
        
        // Verify the resource (best effort)
        const isValid = await verifyResource(resource.url)
        resource.is_verified = isValid
        resource.is_placeholder = !isValid
      }
    }
  }
  
  return enrichedRoadmap
}

// Curated resource database for common topics
export const CURATED_RESOURCES = {
  python: [
    {
      title: "Python for Beginners - Full Course",
      url: "https://www.youtube.com/watch?v=rfscVS0vtbw",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "4h 26m"
    },
    {
      title: "Python Official Documentation",
      url: "https://docs.python.org/3/tutorial/",
      type: "doc",
      platform: "Python.org",
      is_free: true
    }
  ],
  javascript: [
    {
      title: "JavaScript Full Course for Beginners",
      url: "https://www.youtube.com/watch?v=PkZNo7MFNFg",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "8h 0m"
    },
    {
      title: "MDN JavaScript Guide",
      url: "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide",
      type: "doc",
      platform: "MDN",
      is_free: true
    }
  ],
  react: [
    {
      title: "React Course - Beginner's Tutorial",
      url: "https://www.youtube.com/watch?v=bMknfKXIFA8",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "11h 55m"
    },
    {
      title: "React Official Documentation",
      url: "https://react.dev/learn",
      type: "doc",
      platform: "React.dev",
      is_free: true
    }
  ],
  dataScience: [
    {
      title: "Data Science Full Course",
      url: "https://www.youtube.com/watch?v=ua-CiDNNj30",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "12h 0m"
    },
    {
      title: "Kaggle Learn",
      url: "https://www.kaggle.com/learn",
      type: "course",
      platform: "Kaggle",
      is_free: true
    }
  ],
  machineLearning: [
    {
      title: "Machine Learning Course - Stanford",
      url: "https://www.coursera.org/learn/machine-learning",
      type: "course",
      platform: "Coursera",
      is_free: true,
      duration: "11 weeks"
    },
    {
      title: "Machine Learning for Beginners",
      url: "https://www.youtube.com/watch?v=gmvvaobm7eQ",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "3h 0m"
    }
  ],
  webDevelopment: [
    {
      title: "Full Stack Web Development for Beginners",
      url: "https://www.youtube.com/watch?v=nu_pCVPKzTk",
      type: "video",
      platform: "YouTube",
      is_free: true,
      duration: "7h 0m"
    },
    {
      title: "The Odin Project",
      url: "https://www.theodinproject.com/",
      type: "course",
      platform: "The Odin Project",
      is_free: true
    }
  ]
}

// Export roadmap as JSON
export const exportAsJSON = (roadmap) => {
  const dataStr = JSON.stringify(roadmap, null, 2)
  const dataBlob = new Blob([dataStr], { type: 'application/json' })
  const url = URL.createObjectURL(dataBlob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = `${roadmap.title.replace(/\s+/g, '_')}_roadmap.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Export roadmap as PDF (using jsPDF) - Enhanced version
export const exportAsPDF = async (roadmap) => {
  try {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF()
    
    let yPosition = 20
    const lineHeight = 7
    const pageHeight = doc.internal.pageSize.height
    const pageWidth = doc.internal.pageSize.width
    const margin = 20
    const maxWidth = pageWidth - (margin * 2)
    
    // Helper to safely get value or default
    const safeValue = (value, defaultValue = 'Not specified') => {
      return value || defaultValue
    }
    
    // Helper to add new page if needed
    const checkPageBreak = (extraSpace = 0) => {
      if (yPosition + extraSpace > pageHeight - margin) {
        doc.addPage()
        yPosition = 20
        return true
      }
      return false
    }
    
    // Add header with branding
    doc.setFillColor(139, 92, 246) // Violet color
    doc.rect(0, 0, pageWidth, 15, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(12)
    doc.setFont(undefined, 'bold')
    doc.text('PathPilot - AI-Powered Learning Roadmap', margin, 10)
    doc.setTextColor(0, 0, 0)
    yPosition = 25
    
    // Title with background
    doc.setFillColor(243, 244, 246)
    doc.roundedRect(margin - 5, yPosition - 5, maxWidth + 10, 15, 3, 3, 'F')
    doc.setFontSize(20)
    doc.setFont(undefined, 'bold')
    doc.text(safeValue(roadmap.title, 'Untitled Roadmap'), margin, yPosition + 5)
    yPosition += 25
    
    // Metadata section with icons
    doc.setFontSize(10)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(75, 85, 99)
    
    // Duration
    doc.setFont(undefined, 'bold')
    doc.text('📅 Duration:', margin, yPosition)
    doc.setFont(undefined, 'normal')
    doc.text(`${safeValue(roadmap.duration_weeks, 0)} weeks`, margin + 30, yPosition)
    yPosition += lineHeight
    
    // Topic
    doc.setFont(undefined, 'bold')
    doc.text('🎯 Topic:', margin, yPosition)
    doc.setFont(undefined, 'normal')
    doc.text(safeValue(roadmap.topic, 'General Learning'), margin + 30, yPosition)
    yPosition += lineHeight
    
    // Confidence (if available)
    if (roadmap.confidence) {
      doc.setFont(undefined, 'bold')
      doc.text('⭐ Confidence:', margin, yPosition)
      doc.setFont(undefined, 'normal')
      doc.text(`${Math.round(roadmap.confidence * 100)}%`, margin + 30, yPosition)
      yPosition += lineHeight
    }
    
    yPosition += lineHeight
    doc.setTextColor(0, 0, 0)
    
    // Why this fits section
    if (roadmap.why_this_fits) {
      checkPageBreak(20)
      doc.setFillColor(254, 243, 199) // Amber background
      doc.roundedRect(margin - 5, yPosition - 3, maxWidth + 10, 8, 2, 2, 'F')
      doc.setFontSize(12)
      doc.setFont(undefined, 'bold')
      doc.text('💡 Why This Roadmap Works For You', margin, yPosition + 3)
      yPosition += 12
      
      doc.setFontSize(10)
      doc.setFont(undefined, 'normal')
      const splitText = doc.splitTextToSize(safeValue(roadmap.why_this_fits), maxWidth - 10)
      doc.text(splitText, margin + 5, yPosition)
      yPosition += lineHeight * splitText.length + lineHeight
    }
    
    checkPageBreak()
    
    // Weeks section
    if (roadmap.weeks && Array.isArray(roadmap.weeks)) {
      for (const week of roadmap.weeks) {
        checkPageBreak(30)
        
        // Week header with colored background
        doc.setFillColor(219, 234, 254) // Blue background
        doc.roundedRect(margin - 5, yPosition - 3, maxWidth + 10, 10, 2, 2, 'F')
        doc.setFontSize(14)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(30, 64, 175) // Blue text
        const weekTitle = `Week ${safeValue(week.week, '?')}${week.theme ? ': ' + week.theme : ''}`
        doc.text(weekTitle, margin, yPosition + 4)
        doc.setTextColor(0, 0, 0)
        yPosition += 15
        
        // Tasks
        if (week.tasks && Array.isArray(week.tasks)) {
          for (const task of week.tasks) {
            checkPageBreak(25)
            
            // Task title
            doc.setFontSize(11)
            doc.setFont(undefined, 'bold')
            doc.text(`✓ ${safeValue(task.title, 'Untitled Task')}`, margin + 5, yPosition)
            yPosition += lineHeight
            
            // Task description
            doc.setFontSize(9)
            doc.setFont(undefined, 'normal')
            doc.setTextColor(75, 85, 99)
            if (task.description) {
              const descSplit = doc.splitTextToSize(task.description, maxWidth - 20)
              doc.text(descSplit, margin + 10, yPosition)
              yPosition += lineHeight * descSplit.length
            }
            
            // Time and deliverable
            doc.setTextColor(0, 0, 0)
            const timeText = `⏱️ Time: ${safeValue(task.est_hours, 0)}h`
            const deliverableText = `📦 Deliverable: ${safeValue(task.deliverable, 'Complete task')}`
            doc.text(timeText, margin + 10, yPosition)
            yPosition += lineHeight
            doc.text(deliverableText, margin + 10, yPosition)
            yPosition += lineHeight
            
            // Resources with clickable links
            if (task.resources && task.resources.length > 0) {
              doc.setFont(undefined, 'bold')
              doc.text('📚 Resources:', margin + 10, yPosition)
              yPosition += lineHeight
              doc.setFont(undefined, 'normal')
              
              for (const resource of task.resources) {
                checkPageBreak()
                const resourceTitle = safeValue(resource.title, 'Resource')
                const resourcePlatform = safeValue(resource.platform, 'Web')
                const resourceText = `  • ${resourceTitle} (${resourcePlatform})`
                
                // Add clickable link if URL exists
                if (resource.url) {
                  doc.setTextColor(37, 99, 235) // Blue for links
                  doc.textWithLink(resourceText, margin + 15, yPosition, { url: resource.url })
                  doc.setTextColor(0, 0, 0)
                } else {
                  doc.text(resourceText, margin + 15, yPosition)
                }
                yPosition += lineHeight
              }
            }
            
            yPosition += lineHeight * 0.5
          }
        }
        
        yPosition += lineHeight
      }
    }
    
    // Footer on last page
    const totalPages = doc.internal.pages.length - 1
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(156, 163, 175)
      doc.text(`Generated by PathPilot | Page ${i} of ${totalPages}`, margin, pageHeight - 10)
      doc.text(new Date().toLocaleDateString(), pageWidth - margin - 30, pageHeight - 10)
    }
    
    // Save with safe filename
    const safeFilename = (roadmap.title || 'roadmap').replace(/[^a-z0-9]/gi, '_').toLowerCase()
    doc.save(`${safeFilename}_pathpilot.pdf`)
    
    return { success: true, error: null }
  } catch (error) {
    console.error('Error exporting PDF:', error)
    return { success: false, error: error.message }
  }
}

// Calculate roadmap progress
export const calculateProgress = (roadmap, completedTasks = []) => {
  let totalTasks = 0
  let completed = 0
  
  for (const week of roadmap.weeks) {
    totalTasks += week.tasks.length
    for (const task of week.tasks) {
      if (completedTasks.includes(task.id)) {
        completed++
      }
    }
  }
  
  return {
    total: totalTasks,
    completed,
    percentage: totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0
  }
}

// Validate roadmap structure
export const validateRoadmap = (roadmap) => {
  const errors = []
  
  if (!roadmap.title) errors.push('Missing title')
  if (!roadmap.topic) errors.push('Missing topic')
  if (!roadmap.duration_weeks) errors.push('Missing duration')
  if (!roadmap.weeks || !Array.isArray(roadmap.weeks)) errors.push('Missing or invalid weeks array')
  
  if (roadmap.weeks) {
    roadmap.weeks.forEach((week, idx) => {
      if (!week.week) errors.push(`Week ${idx + 1}: Missing week number`)
      if (!week.tasks || !Array.isArray(week.tasks)) errors.push(`Week ${idx + 1}: Missing or invalid tasks`)
      
      if (week.tasks) {
        week.tasks.forEach((task, taskIdx) => {
          if (!task.title) errors.push(`Week ${idx + 1}, Task ${taskIdx + 1}: Missing title`)
          if (!task.resources || task.resources.length === 0) {
            errors.push(`Week ${idx + 1}, Task ${taskIdx + 1}: No resources provided`)
          }
        })
      }
    })
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}
